import React, { useEffect, useState } from 'react';
import Grid from '@jbhi-fi/lanyard-ui/components/Grid';
import { classNames } from '@jbhi-fi/lanyard-ui/global/utils';
import { ServiceIcon } from '../../../templates/components/pdp/productService/icons/ServiceIcon';
import DeleteIcon from '@jbhi-fi/lanyard-ui/components/Icons/Delete';
import { formatMoney } from '../../../utils/FormatMoney';
import { trackEvent } from '../../../utils/event-tracking/track-event';
import { dataLayerPushServiceEvent } from '../../../templates/components/pdp/utils/utils';
import { isPdpServiceSelected } from '../../../templates/components/pdp/utils/product-service';
import { useShopifyProduct } from '../../../api/shopify/ShopifyProduct';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { ProductGtmData } from '@jbhi-fi/apis/src/algolia/types/ProductGtmData';
import { styles } from './AttachedService.css';
import { JbCartItem } from '@jbhi-fi/apis/src/shopify/types/JbCart';

export type AttachedServiceProps = {
  offeringName: string;
  offeringSku: string;
  offeringType: string;
  vendorName: string;
  priceInc: number;
  onRemove: (e: React.MouseEvent<HTMLElement>) => Promise<void>;
  variant?: 'wide' | 'narrow';
  showRemove?: boolean;
  isValid?: boolean;
  gtmTrackingData?: ProductGtmData;
  primaryItem: JbCartItem;
  extraCareCoveragePeriod?: string;
  imageUrl: string;
  isCartPage?: boolean;
  isCheckout?: boolean;
};

export const AttachedService = ({
  offeringName,
  offeringSku,
  offeringType,
  priceInc,
  vendorName,
  onRemove,
  variant = 'narrow',
  showRemove = true,
  isValid = true,
  gtmTrackingData,
  primaryItem,
  extraCareCoveragePeriod,
  imageUrl,
}: AttachedServiceProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { shopify } = useShopifyProduct();

  useEffect(() => {
    if (!isValid) {
      trackEvent({
        event: 'showInvalidAttachedService',
        product: gtmTrackingData,
        primaryItem: {
          sku: primaryItem.sku,
          title: primaryItem.product_title,
          linePrice: primaryItem.line_price,
          originalPrice: primaryItem.original_line_price,
        },
      });
    }
  }, []);

  const priceFormatted = formatMoney(priceInc);
  const isNarrow = variant === 'narrow';
  const gtmSource = window?.gtm?.pageType === 'Checkout Pages' ? 'cart' : 'minicart';

  const handleRemove = async (event: React.MouseEvent<HTMLElement>) => {
    if (isLoading) return;
    // if viewing PDP for primary item
    if (shopify?.variants[0]?.sku === primaryItem.sku && isPdpServiceSelected(offeringType, primaryItem.sku)) {
      const pdpDeselectEvt = new CustomEvent('JB-pdp-service-deselect', {
        detail: {
          id: offeringType,
          sku: offeringSku,
          name: offeringName,
          priceInc: priceInc,
          source: gtmSource,
        },
      });
      window.dispatchEvent(pdpDeselectEvt);
    }
    setIsLoading(true);
    try {
      await onRemove(event);
      dataLayerPushServiceEvent(
        `${offeringType}.removedfromcart`,
        gtmTrackingData,
        {
          sku: offeringSku,
          name: offeringName,
          price: priceInc,
        },
        gtmSource
      );
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  };

  return (
    <React.Fragment>
      <Grid className={styles.wrapper({ isNarrow })} container gutter="narrow">
        <Grid item xs={9} md={isNarrow ? 9 : 9}>
          <div className={classNames([styles.container, !isValid ? 'invalid-attached-service' : null])}>
            <div className={styles.image}>
              <ServiceIcon imageUrl={imageUrl} size={42} />
            </div>
            <div>
              <div className={classNames([styles.text, styles.title])}>{offeringName}</div>
              {extraCareCoveragePeriod ? (
                <div
                  dangerouslySetInnerHTML={{ __html: extraCareCoveragePeriod }}
                  className={classNames([styles.text, styles.warrantyPeriod({ isNarrow })])}
                ></div>
              ) : (
                <div className={classNames([styles.text, styles.vendor])}>Issued by {vendorName}</div>
              )}
            </div>
          </div>
        </Grid>
        <Grid item xs={3} md={isNarrow ? 3 : 3}>
          <div className={styles.actionsContainer({ isNarrow })}>
            <div className={styles.removeButton}>
              {showRemove ? (
                isLoading ? (
                  'Removing...'
                ) : (
                  <Button
                    type="linkWithIcon"
                    iconPosition="left"
                    icon={<DeleteIcon />}
                    label="Remove"
                    onClick={handleRemove}
                  />
                )
              ) : null}
            </div>
            <div className={classNames([styles.text, styles.price({ isNarrow })])}>{priceFormatted}</div>
          </div>
        </Grid>
      </Grid>
      {!isValid && (
        <div className={styles.invalidContainer}>
          <span>This service is no longer available for this item. Please remove to continue.</span>
          <button className={styles.invalidButton} type="button" onClick={handleRemove}>
            {isLoading ? 'Removing...' : 'Remove'}
          </button>
        </div>
      )}
    </React.Fragment>
  );
};
